import { isDefined } from "../utils";
import { Ichimoku as defaultOptions } from "./defaultOptionsForComputation";
import offsetSeries from "./offsetSeries";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { conversionPeriod, basePeriod, spanBPeriod, displacement } = options;

    if (
      !data ||
      data.length <
        Math.max(conversionPeriod, basePeriod, spanBPeriod) + displacement
    ) {
      return [];
    }

    const len = data.length;

    // Helper function to get highest high and lowest low over period
    function getHighLow(period, index) {
      let high = -Infinity;
      let low = Infinity;

      for (let j = index - period + 1; j <= index; j++) {
        if (j < 0 || !data[j]) continue;
        if (isDefined(data[j].high)) high = Math.max(high, data[j].high);
        if (isDefined(data[j].low)) low = Math.min(low, data[j].low);
      }

      return { high, low };
    }

    // Passo 1 — séries por barra, SEM deslocamento.
    const valid = new Array(len);
    const conversion = new Array(len);
    const base = new Array(len);
    const spanARaw = new Array(len);
    const spanBRaw = new Array(len);
    const closeRaw = new Array(len);

    for (let i = 0; i < len; i++) {
      const bar = data[i];

      valid[i] =
        !!bar &&
        isDefined(bar.high) &&
        isDefined(bar.low) &&
        isDefined(bar.close);
      if (!valid[i]) continue;

      // 1. Conversion Line (Tenkan-sen) = (HHV(9) + LLV(9)) / 2
      if (i >= conversionPeriod - 1) {
        const { high, low } = getHighLow(conversionPeriod, i);
        conversion[i] = (high + low) / 2;
      }

      // 2. Base Line (Kijun-sen) = (HHV(26) + LLV(26)) / 2
      if (i >= basePeriod - 1) {
        const { high, low } = getHighLow(basePeriod, i);
        base[i] = (high + low) / 2;
      }

      // 3. Leading Span A (Senkou Span A) = (Conversion + Base) / 2
      if (isDefined(conversion[i]) && isDefined(base[i])) {
        spanARaw[i] = (conversion[i] + base[i]) / 2;
      }

      // 4. Leading Span B (Senkou Span B) = (HHV(52) + LLV(52)) / 2
      if (i >= spanBPeriod - 1) {
        const { high, low } = getHighLow(spanBPeriod, i);
        spanBRaw[i] = (high + low) / 2;
      }

      // 5. Fonte do Lagging Span (Chikou) = fechamento da própria barra
      closeRaw[i] = bar.close;
    }

    // Passo 2 — deslocamento no tempo, o MESMO operador REF do engine
    // (offsetSeries): spans para frente (+d), chikou para trás (−d). O array
    // nunca cresce — o que passa da última barra é truncado, e é o
    // `computeFutureProjection` abaixo que alimenta o overlay futuro (#161).
    const spanA = offsetSeries(spanARaw, displacement);
    const spanB = offsetSeries(spanBRaw, displacement);
    const laggingSpan = offsetSeries(closeRaw, -displacement);

    // Passo 3 — montagem. Barra inválida zera tudo que é DELA, inclusive os
    // spans que chegariam de i−displacement (o algoritmo original recriava o
    // objeto naquele índice); já o laggingSpan vem da barra i+displacement, é
    // escrito DEPOIS e portanto sobrevive. Ordem de escrita preservada.
    const results = new Array(len);
    for (let i = 0; i < len; i++) {
      results[i] = valid[i]
        ? {
            conversionLine: conversion[i],
            baseLine: base[i],
            spanA: spanA[i],
            spanB: spanB[i],
            laggingSpan: laggingSpan[i],
          }
        : {
            conversionLine: undefined,
            baseLine: undefined,
            spanA: undefined,
            spanB: undefined,
            laggingSpan: laggingSpan[i],
          };
    }

    return results;
  }

  calculator.undefinedLength = function () {
    const { basePeriod, displacement } = options;
    return basePeriod + displacement - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  // Returns the {spanA, spanB, sourceIndex} for the `displacement` future bars
  // that calculator(data) silently discards via the `i + displacement < len` gate
  // at lines 94 and 108. Used by IchimokuFutureCloudOverlay (issue #161).
  calculator.computeFutureProjection = function (data) {
    const { conversionPeriod, basePeriod, spanBPeriod, displacement } = options;

    if (
      !data ||
      data.length <
        Math.max(conversionPeriod, basePeriod, spanBPeriod) + displacement
    ) {
      return [];
    }

    const len = data.length;
    const projection = new Array(displacement);

    function getHighLow(period, index) {
      let high = -Infinity;
      let low = Infinity;
      for (let j = index - period + 1; j <= index; j++) {
        if (j < 0 || !data[j]) continue;
        if (isDefined(data[j].high)) high = Math.max(high, data[j].high);
        if (isDefined(data[j].low)) low = Math.min(low, data[j].low);
      }
      return { high, low };
    }

    for (let k = 0; k < displacement; k++) {
      const sourceIndex = len - displacement + k;
      const bar = data[sourceIndex];

      let spanA;
      let spanB;

      if (
        bar &&
        isDefined(bar.high) &&
        isDefined(bar.low) &&
        isDefined(bar.close)
      ) {
        let conversionLine;
        if (sourceIndex >= conversionPeriod - 1) {
          const { high, low } = getHighLow(conversionPeriod, sourceIndex);
          conversionLine = (high + low) / 2;
        }

        let baseLine;
        if (sourceIndex >= basePeriod - 1) {
          const { high, low } = getHighLow(basePeriod, sourceIndex);
          baseLine = (high + low) / 2;
        }

        if (isDefined(conversionLine) && isDefined(baseLine)) {
          spanA = (conversionLine + baseLine) / 2;
        }

        if (sourceIndex >= spanBPeriod - 1) {
          const { high, low } = getHighLow(spanBPeriod, sourceIndex);
          spanB = (high + low) / 2;
        }
      }

      projection[k] = { spanA, spanB, sourceIndex };
    }

    return projection;
  };

  calculator.requestedFutureBars = function () {
    return options.displacement;
  };

  return calculator;
}
